/**
 * Light or dark, and remembering which.
 *
 * The choice can come from two places: the person set it here, or the host
 * page this application is embedded in announced its own. The host wins while
 * embedded, because a builder that renders dark inside a light portal looks
 * broken rather than customised.
 *
 * The preference is written to `localStorage`, which is the right place for
 * it: it is not private, it should survive a refresh, and losing it is a
 * cosmetic inconvenience rather than lost work.
 */

import { defineStore } from 'pinia'
import { ref } from 'vue'

export type ThemeMode = 'dark' | 'light'

const STORAGE_KEY = 'dmbuilder.theme'
/**
 * Light, as the platform this ships beside opens in. A choice somebody made
 * is remembered and still wins; this only decides what a new reader sees.
 */
const DEFAULT: ThemeMode = 'light'

function remembered(): ThemeMode {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY)
    return stored === 'light' || stored === 'dark' ? stored : DEFAULT
  } catch {
    // A browser configured to block site data throws rather than returning
    // null. Not worth failing over: the default is a working theme.
    return DEFAULT
  }
}

function remember(mode: ThemeMode): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, mode)
  } catch {
    // As above. The theme still applies for this session.
  }
}

export const useThemeStore = defineStore('theme', () => {
  const current = ref<ThemeMode>(remembered())

  /** Put the current theme on the document, where the stylesheets read it. */
  function apply(): void {
    document.documentElement.setAttribute('data-theme', current.value)
  }

  function set(mode: ThemeMode): void {
    current.value = mode
    remember(mode)
    apply()
  }

  function toggle(): void {
    set(current.value === 'dark' ? 'light' : 'dark')
  }

  return { current, apply, set, toggle }
})
